import { CloseOutlined } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ManagementPartyDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { DashboardManagement } from './DashboardManagement'
import { NewFormParties } from './NewFormParties'
import { PartyQuotationExchangeChannelsTab } from './PartyQuotationExchangeChannelsTab'

type Props = {
  open: boolean
  data: ManagementPartyDto
  onConfirm: (party: ManagementPartyDto) => Promise<ManagementPartyDto>
  onPartyChanged: (party: ManagementPartyDto) => void
  isNew: boolean
  onClose: () => void
  onCancel?: () => void
  errors?: {
    validation?: {
      id?: string
      name?: string
      coCKey?: string
      currencyCode?: string
      modules?: string
    }
  }
}

const tabA11y = (index: number) => ({
  id: `system-party-tab-${index}`,
  'aria-controls': `system-party-tabpanel-${index}`,
})

export const SystemPartySettingsDialog = (props: Props) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [changedParty, setChangedParty] = useState<ManagementPartyDto>(props.data)
  const [selectedTab, setSelectedTab] = useState(0)
  const [savingParty, setSavingParty] = useState(false)

  useEffect(() => {
    setChangedParty((current) =>
      current.id === props.data.id
        ? {
            ...current,
            quotationExchangeChannels: props.data.quotationExchangeChannels,
          }
        : props.data,
    )
  }, [props.data])

  const handleCancel = () => {
    props.onCancel?.()
    props.onClose()
  }

  const handleChange = <K extends keyof ManagementPartyDto>(
    key: K,
    value: ManagementPartyDto[K],
  ) => setChangedParty((current) => ({ ...current, [key]: value }))

  const saveParty = async (closeAfterSave: boolean) => {
    setSavingParty(true)
    try {
      const savedParty = await props.onConfirm(changedParty)
      setChangedParty(savedParty)
      props.onPartyChanged(savedParty)
      if (closeAfterSave) props.onClose()
    } catch (err) {
      ShowException('party management', err)
    } finally {
      setSavingParty(false)
    }
  }

  const saveFirst = (
    <Alert
      severity="info"
      action={
        <Button
          color="inherit"
          size="small"
          onClick={() => setSelectedTab(0)}
        >
          Go to General
        </Button>
      }
    >
      Save the party in General before configuring this section.
    </Alert>
  )

  return (
    <Dialog
      open={props.open}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      onClose={(_, reason) => {
        if (reason === 'backdropClick' && !savingParty) handleCancel()
      }}
      slotProps={{
        paper: {
          sx: { height: { sm: 'calc(100% - 64px)' } },
        },
      }}
    >
      <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <Box component="header" sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 } }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography id="system-party-settings-title" variant="h6" noWrap sx={{ fontWeight: 600 }}>
                {props.isNew
                  ? t('system:new-party', 'New Party')
                  : changedParty.name || t('system:edit-party', 'Edit Party')}
              </Typography>
              {!props.isNew && (
                <Typography variant="body2" color="text.secondary" noWrap>{changedParty.id}</Typography>
              )}
            </Box>
            <IconButton aria-label="Close party settings" edge="end" disabled={savingParty} onClick={handleCancel}>
              <CloseOutlined />
            </IconButton>
          </Stack>
          <Tabs
            value={selectedTab}
            onChange={(_, value: number) => setSelectedTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ px: { xs: 1, sm: 1.5 }, mt: 1 }}
          >
            <Tab label="General" {...tabA11y(0)} />
            <Tab label="Quotation Channels" {...tabA11y(1)} />
            <Tab label="Dashboards" {...tabA11y(2)} />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Box
            role="tabpanel"
            id={`system-party-tabpanel-${selectedTab}`}
            aria-labelledby={`system-party-tab-${selectedTab}`}
            sx={{ p: { xs: 1.5, sm: 2.5 } }}
          >
            {selectedTab === 0 && (
              <Box
                component="form"
                id="system-party-settings-form"
                onSubmit={(event) => {
                  event.preventDefault()
                  saveParty(true)
                }}
              >
                <NewFormParties data={changedParty} onChange={handleChange} isNew={props.isNew} errors={props.errors} />
              </Box>
            )}
            {selectedTab === 1 &&
              (!props.isNew && changedParty.id ? (
                <PartyQuotationExchangeChannelsTab
                  party={changedParty}
                  onPartyChanged={(party) => {
                    setChangedParty(party)
                    props.onPartyChanged(party)
                  }}
                />
              ) : saveFirst)}
            {selectedTab === 2 &&
              (!props.isNew && changedParty.id ? <DashboardManagement partyId={changedParty.id} /> : saveFirst)}
          </Box>
        </DialogContent>

        <DialogActions sx={{ flexShrink: 0, px: { xs: 2, sm: 2.5 }, py: 1.5, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
          <Button disabled={savingParty} onClick={handleCancel}>Close</Button>
          {selectedTab === 0 && (
            <Button variant="contained" type="submit" form="system-party-settings-form" disabled={savingParty}>
              {savingParty ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  )
}
